import { StyleSheet, css } from 'aphrodite';
import type { FC } from 'react';
import type { CoachMessage } from '../ai';
import { classifySpeaker, parseTranscriptText } from './MessageBlock';

/**
 * The call transcript, newest line first, shown under the live suggestion so the rep can see the AI has
 * heard the prospect before trusting (or asking for) a suggestion.
 */
export const LiveTranscript: FC<{ lines: CoachMessage[] }> = ({ lines }) => (
	<div className={css(styles.root)} data-testid='live-transcript'>
		<div className={css(styles.eyebrow)}>TRANSCRIPT</div>
		{lines.length === 0 ? (
			<div className={css(styles.empty)}>Waiting for the conversation…</div>
		) : (
			<ol className={css(styles.list)}>
				{[...lines].reverse().map(line => {
					const { speaker, body } = parseTranscriptText(line.text);
					const { kind, label } = classifySpeaker(speaker);
					return (
						<li key={line.id} className={css(styles.line)} title={line.time}>
							<span className={css(styles.speaker, kind === 'lead' && styles.speakerLead)}>{label}:</span> {body}
						</li>
					);
				})}
			</ol>
		)}
	</div>
);

const styles = StyleSheet.create({
	root: {
		flexShrink: 0,
		display: 'flex',
		flexDirection: 'column',
		maxHeight: '38%',
		minHeight: 0,
		marginTop: 8,
		paddingTop: 8,
		borderTop: '1px solid var(--border)',
	},
	eyebrow: {
		fontFamily: 'var(--font-mono)',
		fontSize: 9.5,
		fontWeight: 600,
		letterSpacing: '0.1em',
		color: 'var(--ink-faint)',
		marginBottom: 4,
		flexShrink: 0,
	},
	list: {
		listStyle: 'none',
		margin: 0,
		padding: 0,
		overflowY: 'auto',
		minHeight: 0,
	},
	line: {
		fontSize: 12,
		lineHeight: 1.5,
		color: 'var(--ink-muted)',
		marginBottom: 4,
	},
	speaker: {
		fontWeight: 600,
		color: 'var(--ink-muted)',
	},
	speakerLead: {
		color: 'var(--accent)',
	},
	empty: {
		fontSize: 12,
		color: 'var(--ink-faint)',
	},
});
